import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import api, { errMsg, imageSrc, limitInfo } from "@/lib/api";
import { EmptyState } from "@/components/EmptyState";
import { Eyebrow } from "@/components/Eyebrow";
import { toast } from "@/components/ui/sonner";
import { Check, X, Wand2, Loader2, ChevronLeft, ChevronRight, Layers, Sparkles } from "lucide-react";

const PLATFORM_LABEL = { instagram: "Instagram", facebook: "Facebook", tiktok: "TikTok" };

function slotFor(n) {
  const d = new Date();
  d.setDate(d.getDate() + n + 1);
  d.setHours(9, 0, 0, 0);
  return d.toISOString();
}
const dayLabel = (iso) => new Date(iso).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });

export function ReviewDeck({ images, onLimit, refreshUsage, onApproved }) {
  const deck = useMemo(
    () => (images || []).filter((g) => g.status !== "rendering" && (g.items || []).length > 0),
    [images]
  );
  const [processed, setProcessed] = useState(() => new Set());
  const remaining = deck.filter((g) => !processed.has(g.tempId));
  const card = remaining[0];

  const approvedCount = useRef(0);
  const [approvedTotal, setApprovedTotal] = useState(0);

  const [active, setActive] = useState("instagram");
  const [captions, setCaptions] = useState({});
  const [items, setItems] = useState([]);
  const [slide, setSlide] = useState(0);
  const [instruction, setInstruction] = useState("");
  const [revising, setRevising] = useState(false);
  const [variations, setVariations] = useState(null);
  const [busy, setBusy] = useState(false);
  const [exitDir, setExitDir] = useState(0);

  useEffect(() => {
    if (!card) return;
    const plats = card.platforms && card.platforms.length ? card.platforms : ["instagram"];
    setActive(plats[0]);
    setCaptions(card.captions && Object.keys(card.captions).length ? { ...card.captions } : { [plats[0]]: card.caption || "" });
    setItems(card.items || []);
    setSlide(0);
    setInstruction("");
    setVariations(null);
    setRevising(false);
  }, [card?.tempId]);

  const platforms = card && card.platforms && card.platforms.length ? card.platforms : ["instagram"];
  const activeCaption = captions[active] ?? card?.caption ?? "";

  const advance = useCallback((tempId) => setProcessed((prev) => new Set(prev).add(tempId)), []);

  const logEvent = (type, c) => api.post("/memory/event", {
    type, brandId: c.brandId, title: c.title, caption: captions[active] || c.caption,
    assetType: c.assetType, platforms,
  }).catch(() => {});

  const skip = () => {
    if (!card || busy) return;
    logEvent("skip", card);
    setExitDir(-1);
    toast("Skipped — noted for next time", { description: card.title });
    advance(card.tempId);
  };

  const approve = async () => {
    if (!card || busy) return;
    setBusy(true);
    try {
      const cover = items[slide] || items[0] || {};
      const when = slotFor(approvedCount.current);
      await api.post("/schedule", {
        imageId: cover.id,
        imageUrl: imageSrc(cover.url),
        caption: activeCaption,
        captions,
        platforms,
        scheduledAt: when,
      });
      approvedCount.current += 1;
      setApprovedTotal((n) => n + 1);
      logEvent("approve", card);
      setExitDir(1);
      toast.success("Approved → queued to Calendar", { description: dayLabel(when) });
      refreshUsage?.();
      onApproved?.();
      advance(card.tempId);
    } catch (e) {
      const info = limitInfo(e);
      if (info) onLimit?.(info);
      else toast.error(errMsg(e, "Could not approve"));
    } finally {
      setBusy(false);
    }
  };

  const revise = async () => {
    if (!card || !instruction.trim() || revising) return;
    setRevising(true);
    try {
      const { data } = await api.post("/posts/revise", {
        instruction: instruction.trim(),
        brandId: card.brandId,
        visualDirection: card.visualDirection || "",
        caption: activeCaption,
        assetType: card.assetType,
        colors: card.colors || [],
        siteName: card.siteName || "",
        title: card.title || "",
        platform: active,
      });
      setCaptions((prev) => ({ ...prev, [active]: data.caption }));
      const groupId = data.groupId;
      let attempts = 0;
      const poll = async () => {
        attempts += 1;
        try {
          const { data: g } = await api.get(`/images/group/${groupId}`);
          if (g.status === "done" && (g.items || []).length) {
            setVariations({ groupId, items: g.items });
            setRevising(false);
            refreshUsage?.();
            toast.success(`Caption rewritten + ${g.items.length} new visuals`, { description: "Pick your favorite below." });
          } else if (g.status === "error") {
            setRevising(false);
            toast.error("Visual variations failed — caption was still updated.");
          } else if (attempts < 45) {
            setTimeout(poll, 2000);
          } else {
            setRevising(false);
          }
        } catch {
          if (attempts < 45) setTimeout(poll, 2500);
          else setRevising(false);
        }
      };
      poll();
    } catch (e) {
      setRevising(false);
      const info = limitInfo(e);
      if (info) onLimit?.(info);
      else toast.error(errMsg(e, "Revision failed"));
    }
  };

  const pickVariation = (it) => {
    setItems([it]);
    setSlide(0);
    setVariations(null);
    setInstruction("");
    toast.success("Visual updated — approve or revise again");
  };

  const reRender = async () => {
    if (!cur?.id || revising) return;
    setRevising(true);
    try {
      const { data } = await api.post("/posts/fix-text", { imageId: cur.id });
      const groupId = data.groupId;
      let attempts = 0;
      const poll = async () => {
        attempts += 1;
        try {
          const { data: g } = await api.get(`/images/group/${groupId}`);
          if (g.status === "done" && (g.items || []).length) {
            setItems(g.items);
            setSlide(0);
            setRevising(false);
            refreshUsage?.();
            toast.success("Re-rendered with clean text & branding");
          } else if (g.status === "error") {
            setRevising(false);
            toast.error("Re-render failed — please try again.");
          } else if (attempts < 45) setTimeout(poll, 2000);
          else setRevising(false);
        } catch {
          if (attempts < 45) setTimeout(poll, 2500);
          else setRevising(false);
        }
      };
      poll();
    } catch (e) {
      setRevising(false);
      const info = limitInfo(e);
      if (info) onLimit?.(info);
      else toast.error(errMsg(e, "Re-render failed"));
    }
  };

  const total = deck.length;
  const reviewed = total - remaining.length;

  if (total === 0) {
    return (
      <div data-testid="review-deck">
        <EmptyState
          testId="review-deck-empty"
          icon={Layers}
          title="No posts to review yet"
          hint='Render a few concepts in Create, then come back here to swipe through them — approve to queue, skip to dismiss.'
        />
      </div>
    );
  }

  if (!card) {
    return (
      <div data-testid="review-deck">
        <EmptyState
          testId="review-deck-done"
          icon={Check}
          title="Deck cleared 🎉"
          hint={approvedTotal > 0
            ? `You approved ${approvedTotal} post${approvedTotal > 1 ? "s" : ""} — they're queued on your Calendar.`
            : "You've reviewed everything in this batch. Render more concepts in Create to keep going."}
        />
      </div>
    );
  }

  const cur = items[slide] || items[0] || {};
  const multi = items.length > 1;

  return (
    <div data-testid="review-deck" style={{ maxWidth: 460, margin: "0 auto" }}>
      <div className="flex items-center justify-between" style={{ marginBottom: 14 }}>
        <Eyebrow testId="eyebrow-review">Review deck</Eyebrow>
        <span className="font-mono" data-testid="deck-progress" style={{ fontSize: 11, color: "var(--tmuted)" }}>
          {reviewed + 1} / {total} · {approvedTotal} queued
        </span>
      </div>

      <div style={{ position: "relative", minHeight: 540 }}>
        {/* depth: peek of next card */}
        {remaining[1] && (
          <div className="panel-card" style={{ position: "absolute", inset: 0, transform: "scale(0.95) translateY(14px)", opacity: 0.5, zIndex: 0 }} />
        )}

        <AnimatePresence custom={exitDir}>
          <motion.div
            key={card.tempId}
            data-testid="review-card"
            className="panel-card"
            style={{ position: "relative", zIndex: 2, padding: 14, cursor: busy ? "default" : "grab" }}
            drag={busy || variations ? false : "x"}
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.7}
            whileTap={{ cursor: "grabbing" }}
            initial={{ scale: 0.98, opacity: 0 }}
            animate={{ scale: 1, opacity: 1, x: 0, rotate: 0 }}
            exit={{ x: exitDir * 600, opacity: 0, rotate: exitDir * 12, transition: { duration: 0.28 } }}
            transition={{ type: "spring", stiffness: 320, damping: 28 }}
            onDragEnd={(_, info) => {
              if (info.offset.x > 130) approve();
              else if (info.offset.x < -130) skip();
            }}
          >
            {/* image / variations */}
            {variations ? (
              <div data-testid="variation-picker">
                <div className="font-mono" style={{ fontSize: 11, color: "var(--orange)", marginBottom: 8 }}>PICK A VARIATION</div>
                <div className="grid grid-cols-3 gap-2">
                  {variations.items.map((it, i) => (
                    <button
                      key={it.id}
                      data-testid={`variation-option-${i}`}
                      onClick={() => pickVariation(it)}
                      style={{ padding: 0, border: "1px solid var(--cardborder)", borderRadius: 8, overflow: "hidden", cursor: "pointer", background: "var(--input)" }}
                    >
                      <img src={imageSrc(it.url)} alt={`variation ${i + 1}`} style={{ width: "100%", height: 110, objectFit: "cover", display: "block" }} />
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div style={{ position: "relative", borderRadius: 10, overflow: "hidden", border: "1px solid var(--cardborder)" }}>
                {cur.url ? (
                  <img src={imageSrc(cur.url)} alt={card.title} draggable={false} onError={(e) => { e.currentTarget.style.opacity = "0.15"; }} style={{ width: "100%", height: 300, objectFit: "cover", display: "block", background: "var(--input)" }} />
                ) : (
                  <div style={{ width: "100%", height: 300, background: "var(--input)" }} />
                )}
                {cur.textFlag && !revising && (
                  <div data-testid="deck-text-flag" style={{ position: "absolute", top: 10, left: 10, right: 10, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "8px 10px", borderRadius: 8, background: "rgba(180,30,30,0.92)", backdropFilter: "blur(4px)" }}>
                    <span className="font-mono" style={{ fontSize: 11, color: "#fff", fontWeight: 600 }}>⚠ Text issue detected</span>
                    <button data-testid="deck-rerender-btn" onClick={reRender} className="font-mono" style={{ fontSize: 11, padding: "5px 10px", borderRadius: 6, border: "none", background: "#fff", color: "#b41e1e", cursor: "pointer", fontWeight: 700 }}>Re-render</button>
                  </div>
                )}
                {multi && (
                  <>
                    <button className="carousel-nav" style={{ left: 8 }} onClick={() => setSlide((p) => (p - 1 + items.length) % items.length)} data-testid="deck-prev"><ChevronLeft size={15} /></button>
                    <button className="carousel-nav" style={{ right: 8 }} onClick={() => setSlide((p) => (p + 1) % items.length)} data-testid="deck-next"><ChevronRight size={15} /></button>
                  </>
                )}
                {revising && (
                  <div className="grid-pulse" style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", background: "rgba(0,0,0,0.35)" }}>
                    <div className="font-mono" style={{ fontSize: 12, color: "#fff", display: "flex", alignItems: "center", gap: 8 }}>
                      <Loader2 size={15} className="spin" /> RE-RENDERING…
                    </div>
                  </div>
                )}
              </div>
            )}

            <h3 className="font-display" style={{ fontSize: 16, fontWeight: 600, color: "var(--tprimary)", margin: "12px 0 0" }}>{card.title}</h3>
            <div className="font-mono" style={{ fontSize: 10, color: "var(--tmuted)", marginTop: 3 }}>{card.assetType}{multi ? ` · ${items.length} slides` : ""}</div>
            {card.pillar && (
              <span data-testid="deck-pillar" className="font-mono" style={{ display: "inline-block", marginTop: 7, fontSize: 10, padding: "3px 9px", borderRadius: 999, background: "var(--panel)", border: "1px solid var(--cardborder)", color: "var(--orange)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{card.pillar}</span>
            )}

            {platforms.length > 1 && (
              <div style={{ display: "flex", gap: 6, marginTop: 10 }} data-testid="deck-platform-tabs">
                {platforms.map((p) => {
                  const on = p === active;
                  return (
                    <button
                      key={p}
                      data-testid={`deck-platform-tab-${p}`}
                      onClick={() => setActive(p)}
                      className="font-mono"
                      style={{
                        padding: "5px 10px", borderRadius: 999, fontSize: 11, cursor: "pointer", textTransform: "capitalize",
                        background: on ? "var(--orange)" : "transparent", color: on ? "var(--surface)" : "var(--tsecondary)",
                        border: on ? "none" : "1px solid rgba(24,24,27,0.20)",
                      }}
                    >
                      {PLATFORM_LABEL[p] || p}
                    </button>
                  );
                })}
              </div>
            )}

            <textarea
              data-testid="deck-caption"
              className="dim-input font-body"
              style={{ marginTop: 10, minHeight: 70 }}
              value={activeCaption}
              onChange={(e) => setCaptions((prev) => ({ ...prev, [active]: e.target.value }))}
            />

            {/* revision */}
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              <input
                data-testid="deck-revise-input"
                className="dim-input font-body"
                style={{ flex: 1 }}
                placeholder='Plain-language change — e.g. "make it warmer", "shorter caption"'
                value={instruction}
                disabled={revising}
                onChange={(e) => setInstruction(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") revise(); }}
              />
              <button data-testid="deck-revise-btn" className="btn-secondary font-mono" onClick={revise} disabled={revising || !instruction.trim()} style={{ fontSize: 12, padding: "0 14px" }}>
                {revising ? <Loader2 size={14} className="spin" /> : <Wand2 size={14} />} Revise
              </button>
            </div>

            {/* actions */}
            <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
              <button
                data-testid="deck-skip-btn"
                onClick={skip}
                disabled={busy}
                className="font-body"
                style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 7, padding: "11px 0", borderRadius: 9, background: "transparent", border: "1px solid rgba(24,24,27,0.25)", color: "var(--err)", cursor: "pointer", fontWeight: 600, fontSize: 14 }}
              >
                <X size={17} /> Skip
              </button>
              <button
                data-testid="deck-approve-btn"
                onClick={approve}
                disabled={busy}
                className="font-body"
                style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 7, padding: "11px 0", borderRadius: 9, background: "var(--ok)", border: "none", color: "#0D0D0D", cursor: "pointer", fontWeight: 600, fontSize: 14 }}
              >
                {busy ? <Loader2 size={16} className="spin" /> : <Check size={17} />} Approve
              </button>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="flex items-center justify-center gap-2" style={{ marginTop: 16, color: "var(--tmuted)" }}>
        <Sparkles size={12} />
        <span className="font-body" style={{ fontSize: 11.5 }}>Swipe right to approve · left to skip · or use the buttons</span>
      </div>
    </div>
  );
}
